/** @jsxImportSource @emotion/react */
import { styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { css } from '@emotion/react';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import type { CSSProperties } from 'react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';
import {
  SETTINGS_WIDGET_BODY_TEXT_LIGHT_STYLE,
  SETTINGS_WIDGET_COLORS,
  SETTINGS_WIDGET_PANEL_HEADER_STYLE,
  SETTINGS_WIDGET_PANEL_STYLE,
  SETTINGS_WIDGET_SECTION_INSET_STYLE,
  SETTINGS_WIDGET_SUBHEADING_STYLE,
} from './SettingsStyleLib';
import { SettingsToggle } from './SettingsToggle';

type ToggleSettingsPanelRow = {
  type?: 'toggle';
  title: string;
  body: string;
  enabled?: boolean;
  disabled?: boolean;
  onToggleChange?: (enabled: boolean) => void;
};

type FileUploadSettingsPanelRow = {
  type: 'fileUpload';
  fileName: string;
  actionLabel?: string;
  onActionClick?: () => void;
  actionDisabled?: boolean;
  secondaryActionLabel?: string;
  onSecondaryActionClick?: () => void;
  secondaryActionDisabled?: boolean;
};

export type SettingsPanelRow =
  | ToggleSettingsPanelRow
  | FileUploadSettingsPanelRow;

type SettingsPanelProps = {
  title: string;
  rows: readonly SettingsPanelRow[];
};

const PANEL_WRAPPER_STYLE = css`
  label: PANEL_WRAPPER_STYLE;
  ${SETTINGS_WIDGET_SECTION_INSET_STYLE}
`;

const ROW_STYLE = css`
  label: ROW_STYLE;
  min-height: 80px;
  padding: 18px 20px 13px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid ${SETTINGS_WIDGET_COLORS.border};

  &:last-of-type {
    border-bottom: 0;
  }
`;

const FILE_UPLOAD_ROW_STYLE = css`
  label: FILE_UPLOAD_ROW_STYLE;
  min-height: 72px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  border-bottom: 1px solid ${SETTINGS_WIDGET_COLORS.border};

  &:last-of-type {
    border-bottom: 0;
  }
`;

const TEXT_CONTENT_STYLE = css`
  label: TEXT_CONTENT_STYLE;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ROW_TITLE_STYLE = css`
  label: ROW_TITLE_STYLE;
  ${SETTINGS_WIDGET_SUBHEADING_STYLE}
`;

const ROW_BODY_STYLE = css`
  label: ROW_BODY_STYLE;
  margin-top: 4px;
  ${SETTINGS_WIDGET_BODY_TEXT_LIGHT_STYLE}
`;

const FILE_UPLOAD_BUTTON_STYLE: CSSProperties = {
  minHeight: 32,
  minWidth: 84.85,
  borderRadius: 0,
  background: SETTINGS_WIDGET_COLORS.white,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: SETTINGS_WIDGET_COLORS.text,
  textAlign: 'center',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  fontFamily: FONT.body,
  fontSize: pxToRem(FONT_SIZE.bodySmall),
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.bold,
  lineHeight: 1.167,
  letterSpacing: '0.24px',
  border: `0.5px solid ${SETTINGS_WIDGET_COLORS.text}`,
  padding: '9px 15px 9px 16.85px',
};

const FILE_NAME_STYLE = css`
  label: FILE_NAME_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
  font-style: normal;
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.333;
  letter-spacing: 0.18px;
  color: ${SETTINGS_WIDGET_COLORS.text};
`;

const FILE_UPLOAD_ACTIONS_STYLE = css`
  label: FILE_UPLOAD_ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FILE_UPLOAD_SECONDARY_BUTTON_STYLE: CSSProperties = {
  minHeight: 32,
  minWidth: 84.85,
  borderRadius: 0,
  background: 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#bf3b3b',
  textAlign: 'center',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  fontFamily: FONT.body,
  fontSize: pxToRem(FONT_SIZE.bodySmall),
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.bold,
  lineHeight: 1.167,
  letterSpacing: '0.24px',
  border: 0,
  padding: '9px 0',
};

export const SettingsPanel = ({ title, rows }: SettingsPanelProps) => (
  <div css={PANEL_WRAPPER_STYLE}>
    <div css={SETTINGS_WIDGET_PANEL_STYLE}>
      <div css={SETTINGS_WIDGET_PANEL_HEADER_STYLE}>{title}</div>

      {rows.map((row, index) => {
        if (row.type === 'fileUpload') {
          return (
            <div key={`${row.fileName}-${index}`} css={FILE_UPLOAD_ROW_STYLE}>
              <div css={FILE_UPLOAD_ACTIONS_STYLE}>
                <CommonActionButton
                  style={FILE_UPLOAD_BUTTON_STYLE}
                  disabled={row.actionDisabled}
                  onClick={row.onActionClick}
                >
                  {row.actionLabel ?? 'Browse'}
                </CommonActionButton>
                {row.secondaryActionLabel && (
                  <CommonActionButton
                    style={FILE_UPLOAD_SECONDARY_BUTTON_STYLE}
                    disabled={row.secondaryActionDisabled}
                    onClick={row.onSecondaryActionClick}
                  >
                    {row.secondaryActionLabel}
                  </CommonActionButton>
                )}
              </div>
              <div css={FILE_NAME_STYLE}>{row.fileName}</div>
            </div>
          );
        }

        const enabled = row.enabled ?? true;
        const disabled = row.disabled ?? false;

        return (
          <div key={row.title} css={ROW_STYLE}>
            <div css={TEXT_CONTENT_STYLE}>
              <div css={ROW_TITLE_STYLE}>{row.title}</div>
              <div css={ROW_BODY_STYLE}>{row.body}</div>
            </div>

            <SettingsToggle
              checked={enabled}
              disabled={disabled}
              aria-label={row.title}
              onClick={() => row.onToggleChange?.(!enabled)}
            />
          </div>
        );
      })}
    </div>
  </div>
);
